import React from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

import { Box, Tooltip, IconButton } from '@mui/material';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FlashOnIcon from '@mui/icons-material/FlashOn';

import { ScheduleServiceCommand } from '../../../../../../../../services/command';
import { useScheduleService } from '../../../../../../context/ScheduleServiceContext';
import { SCUIButton } from '../../../../../../../../atomic/atoms';
import { SDF, sdfCls } from './functions.classes';

interface ActionButtonProps {
    title: string;
    IconComponent: React.ElementType;
    backgroundColor: string;
    onClick: () => void;
    disabled?: boolean;
    e2eButtonClass: string;
}

export const Functions: React.FC = () => {
    const { selectedJob, setSelectedJob, setRefreshHistory, setIsEditing } = useScheduleService();
    const { t } = useTranslation();

    const handleEdit = () => {
        setIsEditing(true);
    };

    const actionButtons: ActionButtonProps[] = [
        {
            title: t('scheduleService.startService'),
            IconComponent: PlayArrowIcon,
            backgroundColor: 'rgb(3, 169, 244)',
            e2eButtonClass: SDF.actionButtonStart,
            disabled: selectedJob?.isEnabled,
            onClick: () => {
                ScheduleServiceCommand.ResumeSchedule({
                    jobId: selectedJob?.jobId,
                })
                    .then((res: any) => {
                        if (res) {
                            setSelectedJob({ ...selectedJob, isEnabled: true });
                            Swal.fire({
                                position: 'center',
                                icon: 'success',
                                title: t('general.success'),
                                showConfirmButton: false,
                                timer: 1500,
                            });
                        }
                    })
                    .catch((error: any) => Swal.fire(error.message || t('general.anErrorOccured'), '', 'error'));
            },
        },
        {
            title: t('scheduleService.stopService'),
            IconComponent: StopIcon,
            backgroundColor: '#e74c3c',
            e2eButtonClass: SDF.actionButtonStop,
            disabled: !selectedJob?.isEnabled,
            onClick: () => {
                ScheduleServiceCommand.StopSchedule({
                    jobId: selectedJob?.jobId,
                })
                    .then((res: any) => {
                        if (res) {
                            setSelectedJob({ ...selectedJob, isEnabled: false });
                            Swal.fire({
                                position: 'center',
                                icon: 'success',
                                title: t('general.success'),
                                showConfirmButton: false,
                                timer: 1500,
                            });
                        }
                    })
                    .catch((error: any) => Swal.fire(error.message || t('general.anErrorOccured'), '', 'error'));
            },
        },
        {
            title: t('scheduleService.runService'),
            IconComponent: FlashOnIcon,
            backgroundColor: '#27ae60',
            e2eButtonClass: SDF.actionButtonRun,
            onClick: () => {
                ScheduleServiceCommand.RunSchedule({
                    jobId: selectedJob?.jobId,
                })
                    .then((res: any) => {
                        if (res) {
                            setSelectedJob({ ...selectedJob, isEnabled: true });
                            Swal.fire({
                                position: 'center',
                                icon: 'success',
                                title: t('general.success'),
                                showConfirmButton: false,
                                timer: 1500,
                            });
                            setRefreshHistory(true);
                        }
                    })
                    .catch((error: any) => Swal.fire(error.message || t('general.anErrorOccured'), '', 'error'));
            },
        },
    ];

    const renderActionButton = (button: ActionButtonProps) => (
        <Tooltip title={button.title} key={button.title}>
            <IconButton
                className={sdfCls(SDF.actionButton, button.e2eButtonClass)}
                sx={{
                    backgroundColor: button.backgroundColor,
                    color: 'white',
                    '&:hover': { opacity: 0.8 },
                    '&.Mui-disabled': {
                        backgroundColor: button.backgroundColor,
                    },
                }}
                onClick={button.onClick}
                disabled={button.disabled}
                data-action={button.title}
            >
                <button.IconComponent className={SDF.actionIcon} />
            </IconButton>
        </Tooltip>
    );

    return (
        <Box className={SDF.root} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Box
                className={SDF.actionsRow}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '32px',
                    gap: '24px',
                    padding: '16px 0',
                    borderTop: '1px solid #ddd',
                    borderBottom: '1px solid #ddd',
                }}
            >
                {actionButtons.map(renderActionButton)}
            </Box>
            <SCUIButton
                className={SDF.editButton}
                style={{ marginTop: '32px' }}
                type="primary"
                size="m"
                sizeType="fixed"
                text={t('general.edit')}
                onClick={handleEdit}
            />
        </Box>
    );
};
